import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import download from 'downloadjs';

import { moment } from '@hotjar/insights-common/lib/moment';
import { capitalize } from '@hotjar/insights-common/lib/string';
import { getDownloadFilename } from '@hotjar/webapp/features/Heatmaps/utils/getDownloadName';
import { getReportError } from '@hotjar/webapp/features/Heatmaps/utils/getReportError';
import { getReportState } from '@hotjar/webapp/features/Heatmaps/utils/getReportState';
import { HeatmapHeader } from '@hotjar/webapp/features/Heatmaps/components/HeatmapHeader/HeatmapHeader';
import { Loader, tokens } from '@hotjar/hotsauce';
import { noop } from '@hotjar/webapp/lib/function';
import {
  FindHeatmaps,
  NotInstalled,
} from '@hotjar/webapp/features/Heatmaps/components/EmptyStates';
import { openRelatedRecordingForScreenshot } from '@hotjar/webapp/features/Heatmaps/utils/openRelatedRecordingForScreenshot';
import { OptionsBar } from '@hotjar/webapp/features/Heatmaps/ContinuousCaptureReport/OptionsBar/OptionsBar';
import { OptionsWidget } from '@hotjar/webapp/features/Heatmaps/ContinuousCaptureReport/ReportWidgets/OptionsWidget';
import { Report } from '@hotjar/webapp/features/Heatmaps/ContinuousCaptureReport/Report/Report';
import { UnpinConfirmationModal } from '@hotjar/webapp/features/Heatmaps/ContinuousCaptureReport/Pinner/UnpinConfirmationModal';
import { SearchBar } from '@hotjar/webapp/features/Heatmaps/ContinuousCaptureReport/SearchBar/SearchBar';
import { SideBar } from '@hotjar/webapp/features/Heatmaps/SideBar/SideBar';
import type { ModalConfig } from '@hotjar/webapp/features/Heatmaps/SideBar/SideBar';
import { tagRecording } from '@hotjar/webapp/tracking/tools/hotjar';
import {
  ManualRetakerUrlModal,
  useManualRetaker,
  HEATMAP_DATA_TYPES,
  MANUAL_RETAKER,
  HOTJAR_EVENTS,
  MANUAL_RETAKER_ONLINE,
  findOptionByAmountAndUnit,
  getAbsoluteDateRange,
  useGetTopPages,
  downloadHeatData,
  trackHeatmapViewed,
  SIDEBAR_WIDTH,
  SHORT_LINK_WIDGET_HEIGHT,
  OPTIONS_WIDGET_HEIGHT,
  CONTENT_TOP_PADDING,
  CONTENT_RIGHT_LEFT_PADDING,
  isSinglePageSearch,
  MANUAL_RETAKER_MODES,
  getEventTriggeredOnState,
} from '@hotjar/heatmaps';
import { trackBounce } from '@hotjar/webapp/tracking/utils';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { useCurrentSite } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import { useHeatmapData } from '@hotjar/webapp/domains/heatmaps/hooks/useHeatmapsData';
import { useHeatmapParams } from '@hotjar/webapp/domains/heatmaps/hooks/useHeatmapParams';
import { useHeatmapSidebar } from '@hotjar/webapp/domains/heatmaps/hooks/useHeatmapSidebar';
import type { SidebarItem } from '@hotjar/webapp/domains/heatmaps/hooks/useHeatmapSidebar';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { usePageTitle } from '@hotjar/webapp/hooks/usePageTitle';
import { usePinnedPages } from '@hotjar/webapp/domains/heatmaps/hooks/usePinnedPages';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useTrackingCode } from '@hotjar/webapp/hooks/useTrackingCode';
import * as gettingStartedGuide from '@hotjar/webapp/domains/activation/getting-started-guide';
import * as SegmentTracking from '@hotjar/webapp/domains/common/tracking/segment';
import type { HeatmapParams } from '@hotjar/heatmaps';
import type { Site } from '@hotjar/site';
import {
  convertFromNewUiToQueryFormat,
  convertFromQueryToNewUiFormat,
  DATE_RANGE_TYPE,
} from '@hotjar/global-filters';
import {
  EVENT_TRIGGERED_ON,
  EVENT_TRIGGERS,
  EVENT_UI_POSITIONS,
  HEATMAP_VIEWED_TRIGGERS,
} from '@hotjar/webapp/domains/heatmaps/tracking';
import { useFeatureFlag } from '@hotjar/webapp/hooks/useFeatureFlag';
import { useNavigation } from '@hotjar/navigation';
import { useHeatmapsPerformance } from '@hotjar/webapp/features/Heatmaps/hooks/useHeatmapsPerformance';
import { mark, measureAndLog } from '@hotjar/performance';
import { MARKS } from '@hotjar/webapp/performance';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';

import { ErrorBoundaryForPages, useErrorHandler } from '@hotjar/error-handler';
import { logHeatmapsPageBroken } from '@hotjar/webapp/domains/heatmaps/dd-loggers';
import { useAccountExperiment } from '@hotjar/webapp/hooks/useExperiment';
import {
  FeatureEmptyState,
  EmptyStateKind,
} from '@hotjar/webapp/features/Activation/FeatureEmptyState';
import type { PlanComparisonReferrer } from '../Billing/PlanComparisonModal/types';

const HeatmapsWrapper = styled.div`
  display: flex;
`;

const SidebarWrapper = styled.div<{ topNavHeight: number }>`
  min-width: ${SIDEBAR_WIDTH}px;
  max-width: ${SIDEBAR_WIDTH}px;
  min-height: calc(100vh - 134px);
  position: fixed;
  z-index: 1;
  height: calc(100vh + 2px - ${({ topNavHeight }) => topNavHeight}px);
  background-color: ${tokens.color.ui_background};
  overflow-x: hidden;
  overflow-y: auto;
`;

// At the bottom of the container we want to create some extra space so that
// the short link widget (64px) and our toolbar (40px) do no overlap with the
// heat (there is an extra 16px space so that there is some space between the
// toolbar and the report).
const contentBottomPadding = `calc(${SHORT_LINK_WIDGET_HEIGHT} + ${OPTIONS_WIDGET_HEIGHT} + ${tokens.spacer.x2})`;
const ContentWrapper = styled.div<{ noSidebar: boolean }>`
  margin-left: ${({ noSidebar }) => (noSidebar ? 'unset' : `${SIDEBAR_WIDTH}px`)};
  width: ${({ noSidebar }) => (noSidebar ? '100%' : `calc(100% - ${SIDEBAR_WIDTH}px)`)};
  padding: ${CONTENT_TOP_PADDING} ${CONTENT_RIGHT_LEFT_PADDING} ${contentBottomPadding};
`;

const FullContentLoaderWrapper = styled.div<{ topNavHeight: number }>`
  width: 100%;
  height: calc(
    100vh - calc(${CONTENT_TOP_PADDING} + ${contentBottomPadding}) -
      ${({ topNavHeight }) => topNavHeight}px
  );
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledLoader = styled(Loader)`
  min-height: 80vh;
`;

const EmptyStateWrapper = styled.div`
  padding: ${tokens.spacer.x2pt5} ${tokens.spacer.x5};
`;

const sortByTitle = (itemA: HeatmapAPI.PinnedPage, itemB: HeatmapAPI.PinnedPage): number =>
  itemA.name.localeCompare(itemB.name);

declare global {
  interface Window {
    _hjOpenRelatedRecording: (() => void) | undefined;
  }
}

export interface HeatmapsViewProps {
  currentSite: Site;
  currentUser: { id: number; isDemoUser: boolean };
}

export const HeatmapsView = ({ currentSite, currentUser }: HeatmapsViewProps): JSX.Element => {
  const { t } = useTranslation('heatmaps');
  const handleError = useErrorHandler();
  const {
    params,
    paramsWithDevice,
    updateParams,
    updateHeat,
    updateDevice,
    updateUrlAndMatch,
    updateFilters,
    resetInvalidState,
    clearHeatmapsParams,
  } = useHeatmapParams({ siteId: currentSite.id });
  const [currentTargeting, setCurrentTargeting] = useState<TargetingValue>(params.match);
  const currentGenerationStartTime = useRef<number | null>();
  const [trigger, setTrigger] = useState<string>(HEATMAP_VIEWED_TRIGGERS.URL_SEARCH);
  const [autocompleteRank, setAutocompleteRank] = useState<number | undefined>();
  const [retakeParams, setRetakeParams] = useState<HeatmapAPI.RetakerParams | undefined>(undefined);
  const isFirstRun = useRef(true);
  const isOnDefaultView = params.url === '';
  const shouldUseManualRetaker = useFeatureFlag(MANUAL_RETAKER);
  const shouldUseManualRetakerOnline = useFeatureFlag(MANUAL_RETAKER_ONLINE);
  const isLoadingAfterDeleteScreenshotRef = useRef<boolean>(false);
  const { coveragePercentage } = useTrafficCoverage(currentSite);
  const [hasSearchBarBeenCleared, setHasSearchBarBeenCleared] = useState<boolean>(false);

  const topPages = useGetTopPages(currentSite.id);

  useEffect(() => {
    // Perf: Mark default view initialization timings start

    if (isOnDefaultView) {
      mark(MARKS.HEATMAPS_DEFAULT_VIEW_INITIALIZED);
    }
  }, [isOnDefaultView]);

  const { screenshotData, heatData, takeScreenshotData, deleteScreenshot } = useHeatmapData({
    siteId: currentSite.id,
    deviceType: params.device,
    type: params.type,
    url: params.url,
    matchOperation: params.match,
    jsonQueryString: JSON.stringify(convertFromNewUiToQueryFormat(paramsWithDevice)),
    retakeParams,
  });

  const { getState } = useTrackingCode();
  const { topBarHeight: topNavHeight } = useNavigation();
  const notInstalled = !getState(currentSite?.lastData)?.installed;
  const [isOnCreateNewView, setIsOnCreateNewView] = useState<boolean>(false);
  const track = useTrack();
  const { error } = useNotify();
  const { inVariant1: inEmptyStatesExperiment } = useAccountExperiment(
    'atv_empty_states_2023_03',
    currentSite.account,
    notInstalled,
  );

  const shouldHideSidebar = inEmptyStatesExperiment && notInstalled;

  const { getAllPins, activePinInReport } = usePinnedPages({
    siteId: currentSite.id,
    pattern: params.url,
    matchOperation: params.match,
    jsonQueryString: JSON.stringify(convertFromNewUiToQueryFormat(params.filters)),
    onSearchPinsError: () => error(t('sidebar.search-error')),
  });

  const {
    data: pinnedPages,
    status: pinnedPagesStatus,
    refetch: refetchPinnedPages,
    isError: hasPinnedPagesLoadingFailed,
  } = getAllPins;

  const { dateRange } = params.filters;

  const reportError = getReportError({
    screenshotData,
    heatData,
    takeScreenshotData,
    coveragePercentage,
  });

  const isEmptyView =
    notInstalled === false &&
    isOnDefaultView &&
    !isOnCreateNewView &&
    ((pinnedPagesStatus === 'success' && pinnedPages?.length === 0) || hasPinnedPagesLoadingFailed);

  const isNewReportView = notInstalled === false && isOnDefaultView && isOnCreateNewView;

  const isReportView = notInstalled === false && !isOnDefaultView && dateRange;

  const getTitle = useMemo(() => {
    if (
      activePinInReport?.name &&
      activePinInReport?.matchOperation === currentTargeting &&
      activePinInReport?.jsonQueryString ===
        JSON.stringify(convertFromNewUiToQueryFormat(params.filters))
    ) {
      return activePinInReport.name;
    }

    return t('heatmap-title.new');
  }, [activePinInReport, currentTargeting, params.filters, t]);

  const heatmapsTitle = isEmptyView || notInstalled ? '' : getTitle;

  const reportState = getReportState(screenshotData, heatData, takeScreenshotData);

  const onTargetChange = (newMatch: TargetingValue) => {
    setCurrentTargeting(newMatch);
  };

  const onSearch = (url: string, properties?: Partial<HeatmapAPI.TrackRequest>) => {
    setHasSearchBarBeenCleared(false);
    try {
      if (properties?.trigger) {
        setTrigger(properties?.trigger);
        setAutocompleteRank(properties?.autocompleteRank);
      } else {
        setAutocompleteRank(undefined);
      }
      updateUrlAndMatch(url, currentTargeting);
    } catch (e) {
      if (Boolean(e) && e instanceof Error) {
        handleError(e);
      }
    }
  };

  const onUpsellRequest = (
    referrer: PlanComparisonReferrer,
    additionalTrackingData?: Record<string, string>,
  ) => {
    upsell({ site: currentSite, referrer, additionalTrackingData }).catch(() => noop());
  };

  const onUpsellCoverage = () => {
    upsell({
      site: currentSite,
      referrer: 'heatmaps.no-data',
      additionalTrackingData: {
        shown_traffic_coverage: coveragePercentage.toString(),
      },
    }).then(noop, noop);
  };

  const onRetakeScreenshot = useCallback(
    async (
      newRetakeParams?: HeatmapAPI.RetakerParams,
      isManualRetake?: boolean,
      sourceTrigger?: RetakerTriggerSource,
    ) => {
      try {
        setRetakeParams(
          newRetakeParams
            ? { ...newRetakeParams, online: shouldUseManualRetakerOnline }
            : undefined,
        );

        let retakeTrigger = HEATMAP_VIEWED_TRIGGERS.REFRESH;

        if (isManualRetake) {
          retakeTrigger =
            sourceTrigger === 'shortcut'
              ? HEATMAP_VIEWED_TRIGGERS.MANUAL_RETAKER_SHORTCUT
              : HEATMAP_VIEWED_TRIGGERS.MANUAL_RETAKE;
        }

        if (newRetakeParams && 'recordingId' in newRetakeParams) {
          retakeTrigger = HEATMAP_VIEWED_TRIGGERS.TAKE_NEW;
        }

        setTrigger(retakeTrigger);
        await deleteScreenshot?.mutateAsync({
          siteId: currentSite.id,
          deviceType: params.device,
          url: params.url,
          matchOperation: params.match,
          jsonQueryString: JSON.stringify(convertFromNewUiToQueryFormat(paramsWithDevice)),
        });

        // Sets the value that is checked in the "showSurveyAfterScreenshotRetake"
        // function that runs within an Effect, so we can determine if we should show
        // the post-screenshot retake survey.
        isLoadingAfterDeleteScreenshotRef.current = true;
      } catch (e) {
        error(t('screenshot-update.error'));
      }
    },
    [
      currentSite.id,
      deleteScreenshot,
      params.device,
      params.match,
      params.url,
      paramsWithDevice,
      shouldUseManualRetakerOnline,
      error,
      t,
    ],
  );

  const onManualRetakerSuccess = useCallback(
    (html: string, triggerSource: RetakerTriggerSource) => {
      onRetakeScreenshot({ html }, true, triggerSource);
    },
    [onRetakeScreenshot],
  );

  const {
    handleManualRetakerClick,
    handleManualRetakerUrlModalClose,
    handleManualRetakerUrlSubmit,
    isManualRetakerUrlModalOpen,
  } = useManualRetaker(params.device, onManualRetakerSuccess);

  const shouldOpenManualRetakerOnError =
    dateRange?.type === DATE_RANGE_TYPE.RELATIVE && shouldUseManualRetaker;

  const trackRetry = (eventTrigger: string) => {
    if (currentSite) {
      track.click(
        eventTrigger,
        EVENT_UI_POSITIONS.HEATMAP_GENERATION_ERROR,
        track.getGroupDataForSite(currentSite),
        getEventTriggeredOnState(),
      );
    }
  };

  const onRetry = (): void => {
    if (screenshotData.isError) {
      if (shouldOpenManualRetakerOnError) {
        handleManualRetakerClick(
          (isSinglePageSearch(params.match) && params.url) || '',
          MANUAL_RETAKER_MODES.basicScreenshot,
        );
        trackRetry(EVENT_TRIGGERS.MANUAL_RETAKE);
      } else {
        onRetakeScreenshot();
        trackRetry(EVENT_TRIGGERS.REFRESH);
      }
    }
    if (heatData.isError) {
      heatData.refetch();
    }
  };

  const onDownloadData = async (): Promise<void> => {
    if (screenshotData.data?.screenshotId) {
      try {
        const fileData = await downloadHeatData({
          siteId: currentSite.id,
          screenshotId: screenshotData.data.screenshotId,
          type: params.type,
          url: params.url,
          matchOperation: params.match,
          jsonQueryString: JSON.stringify(convertFromNewUiToQueryFormat(paramsWithDevice)),
        });

        download(
          fileData,
          getDownloadFilename({
            ext: 'csv',
            siteId: currentSite.id.toString(),
            type: params.type,
            device: params.device,
          }),
        );

        track.download(
          capitalize(['Heatmap', params.type, 'Data'].join(' ')),
          'csv',
          track.getGroupDataForSite(currentSite),
          getEventTriggeredOnState(),
        );
      } catch (err) {
        error(t('downloads.error'));
      }
    }
  };

  const handlePinnedPageLoad = (paramUpdates: Partial<HeatmapParams>) => {
    setTrigger(HEATMAP_VIEWED_TRIGGERS.SAVED_HEATMAP);
    updateParams(paramUpdates);
  };

  const handleNewHeatmapClick = () => {
    clearHeatmapsParams();
    setIsOnCreateNewView(true);
  };

  const {
    onItemClick,
    onItemSave,
    onItemRemove,
    onItemEdit,
    itemsPinned: inputPinned,
    itemsPinnedState,
    isReadOnly = false,
  } = useHeatmapSidebar(
    currentSite,
    params.url,
    currentTargeting,
    params.filters,
    handlePinnedPageLoad,
  );

  const renderConfirmationModalComponent = (config: ModalConfig<SidebarItem>) => (
    <UnpinConfirmationModal
      isOpen={config.isOpen}
      onClose={config.onClose}
      onConfirm={config.onConfirm}
      name={config.itemToRemove.name}
      pattern={config.itemToRemove.pattern}
      matchOperation={config.itemToRemove.matchOperation}
    />
  );

  useEffect(() => {
    if (!isNewReportView && isOnDefaultView && pinnedPagesStatus === 'success' && pinnedPages) {
      if (pinnedPages.sort(sortByTitle)[0]) {
        setTrigger(HEATMAP_VIEWED_TRIGGERS.DEFAULT_PAGE);
        updateParams({
          url: pinnedPages[0].pattern,
          match: pinnedPages[0].matchOperation,
          filters: convertFromQueryToNewUiFormat(JSON.parse(pinnedPages[0].jsonQueryString)),
        });
      } else {
        resetInvalidState();
      }
    }
  }, [
    isOnDefaultView,
    pinnedPagesStatus,
    pinnedPages,
    currentSite,
    updateParams,
    resetInvalidState,
    isNewReportView,
  ]);

  useEffect(() => {
    refetchPinnedPages();
  }, [currentSite?.id, refetchPinnedPages]);

  useEffect(() => {
    SegmentTracking.page(EVENT_TRIGGERED_ON.REPORT);
    // We need to clear the retake params whenever the url params change,
    // this prevents us from reusing them and setting/overriding the screenshot
    // for a page that doesn't have data/screenshot.
    setRetakeParams(undefined);
  }, [params]);

  useEffect(() => {
    if (!currentGenerationStartTime.current) {
      currentGenerationStartTime.current = performance.now();
    }

    const trackHeatmapViewedArgs = {
      siteId: currentSite.id,
      heatmapType: params.type,
      deviceType: params.device,
      url: params.url,
      matchOperation: params.match,
      // The property is optional on the request params, and in practice,
      // it would be available when the track is made,
      // so it is safe to do type casting here.
      dateRangeType: dateRange?.type as DateRangeType,
      dateRangeLabel:
        dateRange?.type === DATE_RANGE_TYPE.RELATIVE
          ? findOptionByAmountAndUnit(dateRange.amount, dateRange.unit).label
          : undefined,
      dateRangeStartTimestamp:
        dateRange?.type === DATE_RANGE_TYPE.ABSOLUTE
          ? moment.unix(getAbsoluteDateRange(dateRange).fromTimestamp).toISOString()
          : undefined,
      dateRangeEndTimestamp:
        dateRange?.type === DATE_RANGE_TYPE.ABSOLUTE
          ? moment.unix(getAbsoluteDateRange(dateRange).toTimestamp).toISOString()
          : undefined,
      clicksCount:
        heatData?.data?.type === HEATMAP_DATA_TYPES.CLICK ? heatData.data.clickCount : undefined,
      sessionCount: heatData?.data?.sessionCount,
      trigger,
      autocompleteRank,
      displayType: 'screenshot' as HeatmapAPI.DisplayType,
    };

    if (reportState === 'DATA_FETCHED') {
      currentGenerationStartTime.current = null;
      gettingStartedGuide.markHeatmapAsSeen(currentSite, currentUser.id);
      tagRecording('Viewed Heatmap: Continuous');
      tagRecording(`Heatmap Report trigger: ${trigger}`);
      trackHeatmapViewed({ ...trackHeatmapViewedArgs, resultsFound: true });
      isFirstRun.current = false;
    }

    if (reportState === 'FETCH_ERROR') {
      currentGenerationStartTime.current = null;
      tagRecording(`Heatmap Report Failed: ${reportError}`);
      trackHeatmapViewed({
        ...trackHeatmapViewedArgs,
        resultsFound: false,
        failureReason: reportError,
      });
    }

    /*
      This is port of angular $scope.$on('$stateChangeStart' to detect bounced users
      if user will leave view while data is still fetching then we count is as bounced.
    */
    return () => {
      if (
        reportState === 'FETCHING' &&
        currentGenerationStartTime.current &&
        isFirstRun.current === true
      ) {
        const bounceTime = performance.now() - currentGenerationStartTime.current;
        trackBounce(currentSite, 'heatmap_viewed_continuous', Math.floor(bounceTime));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSite, currentUser.id, reportError, reportState]);

  useEffect(() => {
    // eslint-disable-next-line no-underscore-dangle
    window._hjOpenRelatedRecording = () => {
      if (
        screenshotData?.data?.recordingId &&
        screenshotData?.data?.recordingStartTime &&
        currentSite
      )
        openRelatedRecordingForScreenshot({
          recordingId: screenshotData?.data?.recordingId,
          startTime: screenshotData?.data?.recordingStartTime,
          siteId: currentSite.id,
        });
    };
    return () => {
      // eslint-disable-next-line no-underscore-dangle
      delete window._hjOpenRelatedRecording;
    };
  }, [currentSite, screenshotData]);

  useEffect(() => {
    if (currentTargeting !== params.match) {
      setCurrentTargeting(params.match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.match, setCurrentTargeting]);

  useEffect(() => {
    // Perf: Mark fetching report time

    if (reportState === 'FETCHING') {
      mark(MARKS.HEATMAPS_REPORT_FETCH_START);
    }

    if (reportState === 'DATA_FETCHED') {
      measureAndLog({
        name: MARKS.HEATMAPS_REPORT_FETCH_TIME,
        startMark: MARKS.HEATMAPS_REPORT_FETCH_START,
        addMark: true,
      });
    }
  }, [reportState]);

  useEffect(
    function showSurveyAfterScreenshotRetake() {
      if (reportState === 'DATA_FETCHED' && isLoadingAfterDeleteScreenshotRef.current) {
        isLoadingAfterDeleteScreenshotRef.current = false;
        if (window.hj) {
          const eventName =
            trigger === HEATMAP_VIEWED_TRIGGERS.REFRESH
              ? HOTJAR_EVENTS.HEATMAP_REFRESH
              : HOTJAR_EVENTS.HEATMAP_TAKENEW;
          window.hj('event', eventName);
        }
      }

      if (reportState === 'FETCH_ERROR') {
        isLoadingAfterDeleteScreenshotRef.current = false;
      }
    },
    [reportState, trigger],
  );

  const showFullContentLoader = activePinInReport === null && pinnedPagesStatus === 'loading';
  const isCurrentPagePinned =
    !showFullContentLoader &&
    activePinInReport?.pattern === params.url &&
    activePinInReport?.matchOperation === currentTargeting &&
    activePinInReport?.jsonQueryString ===
      JSON.stringify(convertFromNewUiToQueryFormat(params.filters));

  const isPinButtonDisabled =
    reportState === 'FETCHING' || isNewReportView || hasSearchBarBeenCleared;

  const isReportLoadedSuccessfully = Boolean(
    notInstalled === false &&
      reportState === 'DATA_FETCHED' &&
      screenshotData?.data &&
      heatData?.data,
  );

  return (
    <HeatmapsWrapper>
      {!shouldHideSidebar ? (
        <SidebarWrapper topNavHeight={topNavHeight}>
          <SideBar
            itemsList={inputPinned}
            newItemText={t('sidebar.new-heatmap')}
            listTitle={t('sidebar.saved-heatmaps')}
            errorStateText={t('sidebar.error-state-text')}
            emptyStateText={t('sidebar.empty-state-text')}
            listState={itemsPinnedState}
            isReadOnly={isReadOnly}
            renderConfirmationModalComponent={renderConfirmationModalComponent}
            onItemClick={onItemClick}
            onItemSave={onItemSave}
            onItemRemove={onItemRemove}
            onItemEdit={onItemEdit}
            onNewButtonClick={handleNewHeatmapClick}
          />
        </SidebarWrapper>
      ) : null}
      <ContentWrapper noSidebar={shouldHideSidebar}>
        {showFullContentLoader && (
          <FullContentLoaderWrapper topNavHeight={topNavHeight}>
            <Loader size="xl" />
          </FullContentLoaderWrapper>
        )}

        {!showFullContentLoader && (
          <>
            {!inEmptyStatesExperiment ? (
              <HeatmapHeader
                title={heatmapsTitle}
                isPinButtonDisabled={isPinButtonDisabled}
                shouldHidePinButton={notInstalled || isEmptyView}
                isCurrentPagePinned={isCurrentPagePinned}
                searchPattern={params.url}
                matchOperation={currentTargeting}
                filters={params.filters}
                isReportLoaded={isReportLoadedSuccessfully}
              />
            ) : null}

            {isEmptyView && <FindHeatmaps onFindHeatmaps={() => setIsOnCreateNewView(true)} />}

            {notInstalled && (
              <>
                {inEmptyStatesExperiment ? (
                  <EmptyStateWrapper>
                    <FeatureEmptyState kind={EmptyStateKind.HEATMAPS} />
                  </EmptyStateWrapper>
                ) : (
                  <NotInstalled />
                )}
              </>
            )}

            {(isReportView || isNewReportView) && !isCurrentPagePinned && (
              <SearchBar
                sourceUrl={params.url}
                selectedTargetingValue={currentTargeting}
                urlSuggestions={topPages || undefined}
                onTargetChange={onTargetChange}
                onSubmit={onSearch}
                initiallyOpen={isNewReportView}
                onClear={() => setHasSearchBarBeenCleared(true)}
              />
            )}

            {isReportView && (
              <>
                <OptionsBar
                  onFiltersChange={updateFilters}
                  onUpsellRequest={onUpsellRequest}
                  selectedFiltersInUiFormat={params.filters}
                />
                <OptionsWidget
                  reportState={reportState}
                  clickCount={
                    heatData?.data?.type === HEATMAP_DATA_TYPES.CLICK
                      ? heatData.data.clickCount
                      : undefined
                  }
                  onDeviceTypeChange={updateDevice}
                  onHeatTypeChange={updateHeat}
                  selectedDeviceType={params.device}
                  selectedHeatType={params.type}
                />
                <Report
                  errorType={reportError}
                  heatmapData={heatData?.data}
                  modelState={reportState}
                  notInstalled={notInstalled}
                  onDeviceTypeChange={updateDevice}
                  onFiltersChange={updateFilters}
                  onHeatTypeChange={updateHeat}
                  onRetakeScreenshotClicked={onRetakeScreenshot}
                  onRetry={onRetry}
                  onNavigateToHeatmap={onSearch}
                  onUpsellRequest={onUpsellRequest}
                  onUpgradeCoverage={onUpsellCoverage}
                  onDownloadData={onDownloadData}
                  screenshotData={screenshotData?.data}
                  selectedDateRange={dateRange}
                  selectedDeviceType={params.device}
                  selectedFiltersInUiFormat={params.filters}
                  selectedHeatType={params.type}
                  selectedTargetingValue={params.match}
                  siteId={currentSite.id}
                  source={params.url}
                  jsonQueryString={JSON.stringify(convertFromNewUiToQueryFormat(paramsWithDevice))}
                  shouldOpenManualRetakerOnError={shouldOpenManualRetakerOnError}
                  clickCount={
                    heatData?.data?.type === HEATMAP_DATA_TYPES.CLICK
                      ? heatData.data.clickCount
                      : undefined
                  }
                  effectivePlan={currentSite.effectivePlan}
                />
                <ManualRetakerUrlModal
                  isOpen={isManualRetakerUrlModalOpen}
                  onSubmit={handleManualRetakerUrlSubmit}
                  onClose={handleManualRetakerUrlModalClose}
                />
              </>
            )}
          </>
        )}
      </ContentWrapper>
    </HeatmapsWrapper>
  );
};

export const Heatmaps = (): JSX.Element => {
  const { t } = useTranslation('heatmaps');
  const measure = useHeatmapsPerformance();
  measure.startMeasurement();

  usePageTitle(t('page-title.title'));

  const { data: currentSite } = useCurrentSite();
  const { data: currentUser } = useCurrentUser();

  useEffect(() => {
    if (currentSite && currentUser) {
      measure.finishMeasurement();
    }
  }, [currentSite, currentUser, measure]);

  if (!currentSite || !currentUser) {
    return <StyledLoader size="xl" />;
  }

  return (
    <ErrorBoundaryForPages
      squadSentryTag="heatmaps"
      criticality="major"
      featureName="heatmaps-page"
      onError={(error) => logHeatmapsPageBroken(error.message)}
    >
      <HeatmapsView currentSite={currentSite} currentUser={currentUser} />
    </ErrorBoundaryForPages>
  );
};
